'use client';
import {useMemo, useState} from 'react';
import throwToast from '@/utils/ui/toast';
import MyPlusApi from '@/api/main/my-plus';
import globalDomains from '@/utils/global-domains';
import {getUserCookie} from '@/utils/main';
import {DIRECTORY_LIST_CNT, MAIN_LIST_CNT} from '@/constants/main';

export const useHiddenStreamer = (type = '', location = '') => {
  const [remainCount, setRemainCount] = useState(0);
  const [slicedBroadList, setSlicedBroadList] = useState([]);
  const [hiddenStreamerList, setHiddenStreamerList] = useState([]);

  const handleOnClickHiddenStreamer = (data) => {
    setHiddenStreamer(data);
  };

  const handleOnClickUndoHiddenStreamer = (data) => {
    setHiddenUndoStreamer(data);
  };

  const filteredHiddenStreamerBroadList = useMemo(() => {
    return (list, type, hiddenStreamer) => {
      return list.filter((item) => {
        if (type === 'live') {
          return !hiddenStreamer.includes(item.userId);
        } else {
          // 원저작자 -> 업로더 순으로 우선순위
          if (item.originalUserId) {
            return !hiddenStreamer.includes(item.originalUserId);
          } else {
            return !hiddenStreamer.includes(item.userId);
          }
        }
      });
    };
  }, [type, hiddenStreamerList]); // type과 hiddenStreamerList가 변경될 때만 다시 계산

  const filterBroadList = (
    type = '', // live , vod, catch
    page = 1, //페이지
    list = [], //방송리스트
    isMore = false, //더보기 여부
    hiddenStreamer = [], //스트리머 숨기기 리스트
    setBroadList, //setState 콜백
  ) => {
    //api 로 부터 받은 리스트에서 숨긴스트리머리스트 제외하는 동작
    const filteredBroadList = filteredHiddenStreamerBroadList(
      list,
      type,
      hiddenStreamer,
    );
    // LIVE 60개 / VOD,CATCH 120개
    const LIST_COUNT = type === 'live' ? MAIN_LIST_CNT : DIRECTORY_LIST_CNT;

    //부족한 리스트 붙여주고 자른 리스트가 있을경우,
    if (slicedBroadList.length > 0) {
      //자른 리스트 + 새로 들어온 리스트내에서 중복되는 값을 제거해준다.
      let uniqueBroadList = [
        ...slicedBroadList,
        ...filteredBroadList.filter(
          (item) =>
            !slicedBroadList.some(
              (slicedItem) => slicedItem.userId === item.userId,
            ),
        ),
      ];

      if (LIST_COUNT > uniqueBroadList.length && isMore) {
        //병합한 리스트를 기존 리스트에 붙여준다. LIST_COUNT 단위로 붙여주고 남은 리스트는 자른 리스트상태에 다시 붙여서 관리해준다.
        setBroadList((prevState) => {
          return {
            isMore: isMore,
            list: [...prevState.list, ...uniqueBroadList.slice(0, LIST_COUNT)],
          };
        });
        setRemainCount(LIST_COUNT - uniqueBroadList.length);
        setSlicedBroadList([]);
      } else {
        //병합한 리스트를 기존 리스트에 붙여준다. LIST_COUNT 단위로 붙여주고 남은 리스트는 자른 리스트상태에 다시 붙여서 관리해준다.
        setBroadList((prevState) => {
          return {
            isMore: isMore,
            list: [...prevState.list, ...uniqueBroadList.slice(0, LIST_COUNT)],
          };
        });
        setSlicedBroadList([...uniqueBroadList.slice(LIST_COUNT)]);
      }
    }
    //자른 리스트가 없을경우,
    else {
      //다음 리스트에 자르지 않아도 되는 경우(리스트가 LIST_COUNT 에 맞는경우)
      if (remainCount === 0) {
        //호출된 리스트 api 가 마지막 페이지가 아닐떄
        if (isMore) {
          //랜더링 될 리스트에 숨긴 스트리머가 존재해서 LIST_COUNT 보다 작을때 붙여줘야할 리스트갯수를 추출한다.
          if (LIST_COUNT >= filteredBroadList.length) {
            //부족한 리스트라도 일단 붙여서 노출
            setBroadList((prevState) => ({
              isMore: isMore,
              list:
                page === 1
                  ? [...filteredBroadList]
                  : [...prevState.list, ...filteredBroadList],
            }));
            //붙여줘야할 리스트갯수 추출
            setRemainCount(Number(LIST_COUNT - filteredBroadList.length));
          } else {
            console.log('추출 리스트 0 이지만 어떤 조건에도 들어오지않을때');
          }
        }
        //호출된 리스트 api 가 마지막 페이지 일때
        else {
          setBroadList((prevState) => ({
            isMore: isMore,
            list:
              page === 1
                ? [...filteredBroadList]
                : [...prevState.list, ...filteredBroadList],
          }));
        }
      } else {
        //부족한 리스트 갯수만큼 채워야할때
        setBroadList((prevState) => ({
          isMore: isMore,
          list: [...prevState.list, ...filteredBroadList.slice(0, remainCount)],
        }));
        setSlicedBroadList([...filteredBroadList].slice(remainCount));
        //붙어줘야할 갯수보다 api로부터 받아온 리스트가 작을경우 있는 다시 부족한 부분은 붙여줘야하기때문에 분기처리
        if (remainCount < filteredBroadList.length) {
          setRemainCount(0);
        } else {
          setRemainCount(Number(remainCount - filteredBroadList.length));
        }
      }
    }
  };

  //스트리머 숨기기 실행
  const setHiddenStreamer = async (data) => {
    const {broadcastId, userId, userNickname, listDataType, fileType} = data;
    try {
      const params = {
        bj_id: userId,
        bj_nick: userNickname,
        type: type,
        location: location,
        os: 'pc',
        ...(listDataType && {list_data_type: listDataType}),
        ...(location && location === 'myplus' && {namespace: 'prflivod'}),
        key: getUserCookie('uid'),
      };
      if (type === 'LIVE') {
        params.broad_no = broadcastId;
      } else if (type === 'VOD') {
        params.title_no = broadcastId;
      } else if (type === 'CATCH') {
        params.title_no = broadcastId;
        params.type = fileType;
      } else if (type === 'CATCHSTORY') {
        //캐치스토리섹션에 캐치스토리가 부족하면 캐치가 내려온다.
        if (fileType === 'CATCH') {
          params.title_no = broadcastId;
          params.type = 'VOD';
        } else {
          params.story_idx = broadcastId;
          params.type = 'CATCHSTORY';
        }
      }
      const response = await MyPlusApi.setHiddenStreamer(params);
      if (response) {
        if (response.result === 1) {
          setHiddenStreamerList((prevState) => [...prevState, broadcastId]);
        } else {
          if (response.data.code === -24202) {
            //100명이상 초과했을시 내려오는 에러코드번호
            let replaceMsg = '';
            if (response.data.message) {
              replaceMsg = response.data.message.split('.');
            } else {
              replaceMsg = response.data.message;
            }
            throwToast(
              '',
              'side',
              <>
                <p>
                  {replaceMsg[0]}
                  <br />
                  {replaceMsg[1]}
                </p>
                <div className='btn'>
                  <a
                    href={`${globalDomains.MEMBER_AFREECATV}/app/bj_hide_info.php`}
                    target='_blank'>
                    숨긴 스트리머
                    <br /> 관리
                  </a>
                </div>
              </>,
              5000,
            );
          } else {
            throwToast(response.data.message, '', null, 5000);
          }
        }
      }
    } catch (error) {}
  };

  //스트리머 숨기기 취소
  const setHiddenUndoStreamer = async (data) => {
    const {broadcastId, userId, listDataType, fileType} = data;
    try {
      const params = {
        bj_id: userId,
        type: type,
        location: location,
        ...(listDataType && {list_data_type: listDataType}),
      };
      if (type === 'LIVE') {
        params.broad_no = broadcastId;
      } else if (type === 'VOD') {
        params.title_no = broadcastId;
      } else if (type === 'CATCH') {
        params.title_no = broadcastId;
        params.type = fileType;
      } else if (type === 'CATCHSTORY') {
        //캐치스토리섹션에 캐치스토리가 부족하면 캐치가 내려온다.
        if (fileType === 'CATCH') {
          params.title_no = broadcastId;
          params.type = 'VOD';
        } else {
          params.story_idx = broadcastId;
          params.type = 'CATCHSTORY';
        }
      }

      const response = await MyPlusApi.setHiddenUndoStreamer(params);
      if (response) {
        if (response.result === 1) {
          setHiddenStreamerList((prevState) =>
            prevState.filter((id) => id !== broadcastId),
          );
        } else {
          throwToast(response.data.message);
        }
      }
    } catch (error) {}
  };

  const state = {
    remainCount,
    slicedBroadList,
    hiddenStreamerList,
  };

  const handler = {
    setRemainCount,
    filterBroadList,
    setSlicedBroadList,
    handleOnClickHiddenStreamer,
    handleOnClickUndoHiddenStreamer,
  };

  return {...state, ...handler};
};
